import { MAX_PUZZLE_MOVES } from '@veriverse/game-logic';
import { z } from 'zod';

/**
 * Cat Scholes puzzle API (spec §6.14.4, §8.1). The `cat_spot` object opens the puzzle; runs are
 * submitted and leaderboards read over REST, like the calendar behind the wall calendar.
 */

/** `game` value of the puzzle's rows in `game_scores`. */
export const CAT_SCHOLES_GAME = 'cat-scholes';

export const LEADERBOARD_PERIODS = ['week', 'all'] as const;
export type LeaderboardPeriod = (typeof LEADERBOARD_PERIODS)[number];

/** Rows shown per leaderboard. */
export const LEADERBOARD_SIZE = 10;

export const puzzleLevelIdSchema = z
  .string()
  .min(1)
  .max(48)
  .regex(/^[a-z0-9-]+$/);

/** `POST /api/games/cat-scholes/levels/:level/runs`: the moves of a run, e.g. `"rrud"`. */
export const submitPuzzleRunSchema = z
  .object({
    moves: z
      .string()
      .min(1)
      .max(MAX_PUZZLE_MOVES)
      .regex(/^[udlr]+$/),
  })
  .strict();
export type SubmitPuzzleRunRequest = z.infer<typeof submitPuzzleRunSchema>;

/** `GET /api/games/cat-scholes/leaderboard?level=&period=` (spec §8.1). */
export const leaderboardQuerySchema = z.object({
  level: puzzleLevelIdSchema,
  period: z.enum(LEADERBOARD_PERIODS).default('week'),
});

/** The player's own progress: best moves per level and the cracks earned (spec §6.14.4). */
export interface PuzzleProgressResponse {
  readonly levels: readonly { readonly id: string; readonly best: number | null }[];
  readonly solved: number;
  /** Gold kintsugi cracks on the player's Cat Scholes (0–3). */
  readonly cracks: number;
  /** The player hides from leaderboards (settings, spec §6.14.1). */
  readonly leaderboardOptOut: boolean;
}

export interface PuzzleRunResponse {
  readonly moves: number;
  /** The player's best on this level, this run included. */
  readonly best: number;
  /** This run beat the player's previous best (or was the first). */
  readonly newBest: boolean;
  readonly solved: number;
  readonly cracks: number;
}

export interface LeaderboardEntry {
  readonly rank: number;
  readonly displayName: string;
  readonly score: number;
  readonly me: boolean;
}

export interface LeaderboardResponse {
  readonly level: string;
  readonly period: LeaderboardPeriod;
  readonly entries: readonly LeaderboardEntry[];
  /** The player's own place, also when outside the top or hidden by opt-out. */
  readonly mine: { readonly rank: number; readonly score: number } | null;
}
